import type { NexperienceVariantFragment as NexperienceVariant } from '@data-access/lib/gql/cart/documents'
import type { NexperienceDatesWithTimeslotsFragment } from '@data-access/lib/gql/experience/documents'
import { dateParser } from '@feat/feat-url-state/date'
import { parseLocalDate } from '@utils/helpers'
import { formatTime } from '@utils/utils-format'
import { differenceInMinutes } from 'date-fns'
import { createParser, createSerializer, parseAsString } from 'nuqs/server'
import { getExperiencePriceCategoryLabel } from '@/lib/experience/price-category-label'
import { DEFAULT_TIME_ZONE } from '@/lib/time-zone'

import type { ExperienceDayPrices } from '../_hooks/use-app-experience-day-prices'
import type { ExperienceFilter, SelectedOptions } from './experience-state-types'

export enum FilterType {
  person = 'person',
  date = 'date',
  variant = 'variant',
}

export type EdpTimeslot = {
  id: string
  date: string
  startTime: string
  startTimezone?: string | null
  duration: number
  price: number
}

export function mapDatesToEdpTimeslots(
  dates: NexperienceDatesWithTimeslotsFragment[],
  options: { id: string; priceId?: string | null; price: number }[],
  selectedOptions: SelectedOptions | null,
  dayPrices?: ExperienceDayPrices
): EdpTimeslot[] {
  return dates.flatMap(item => {
    const priceByPriceId = dayPrices?.get(item.date)?.priceByPriceId
    const total = options.reduce((sum, option) => {
      const price =
        (option.priceId ? priceByPriceId?.get(option.priceId) : undefined) ?? option.price
      return sum + price * (selectedOptions?.[option.id] ?? 1)
    }, 0)

    return item.timeslots.map(timeslot => {
      const startZone = timeslot.startDate.timezone ?? DEFAULT_TIME_ZONE
      const startDate = parseLocalDate(timeslot.startDate)
      const endDate = parseLocalDate(timeslot.endDate)

      return {
        id: timeslot.id,
        date: item.date,
        duration: differenceInMinutes(endDate, startDate),
        startTime: formatTime(startDate, startZone),
        startTimezone: startZone,
        price: total,
      }
    })
  })
}

export const selectedOptionsParser = createParser<Record<string, number> | null | undefined>({
  serialize: (value: Record<string, number> | null | undefined): string => {
    if (!value) return ''

    const entries = Object.entries(value)
    if (entries.length === 0) return ''

    return entries.map(([key, count]) => `${key}_${String(count)}`).join('*')
  },
  parse: (value: string | null | undefined): Record<string, number> | undefined => {
    if (!value || typeof value !== 'string') return undefined

    const result: Record<string, number> = {}

    for (const pair of value.split('*')) {
      if (!pair) continue

      const [key, count] = pair.split('_')
      if (!key || count === undefined) continue

      const parsedCount = Number(count)
      if (!Number.isNaN(parsedCount)) {
        result[key] = parsedCount
      }
    }

    return Object.keys(result).length > 0 ? result : undefined
  },
})

export const getExperienceSearchParams = createSerializer({
  ...dateParser,
  selectedVariant: parseAsString,
  selectedOptions: selectedOptionsParser,
})

export function buildTermineHref(basePath: string, searchParams?: string) {
  return `${basePath}termine${searchParams ? `?${searchParams}` : ''}`
}

export function getSelectionFromFilter(
  updatedFilter: ExperienceFilter,
  variants: NexperienceVariant[]
): { name: string; amount: number }[] {
  if (!updatedFilter.selectedOptions) return []

  return Object.entries(updatedFilter.selectedOptions).map(([id, amount]) => ({
    name:
      variants
        .find(variant => variant.id === updatedFilter.selectedVariant)
        ?.options.find(option => option.id === id)?.name ?? 'Unknown',
    amount,
  }))
}

export function getExperienceSelectionLabel(
  filter: ExperienceFilter,
  variants: NexperienceVariant[]
) {
  return getSelectionFromFilter(filter, variants)
    .filter(item => item.amount > 0)
    .map(item => `${item.amount} x ${getExperiencePriceCategoryLabel(item.name)}`)
    .join(', ')
}

type PaxSelectionData = {
  selectedOptions: SelectedOptions | null | undefined
  selectedVariant?: string | null | undefined
}

export const getTotalCount = (
  selectedOptions?: SelectedOptions | null,
  options?: { id: string; groupSize?: number | null }[]
) => {
  if (!selectedOptions) return 0

  return Object.keys(selectedOptions).reduce(
    (sum, optionId) => sum + selectedOptions[optionId] * getGroupSize(optionId, options),
    0
  )
}

export const initSelectedOptions = (options: { id: string; min?: number | null }[]) => {
  return { [options[0]?.id]: options[0]?.min || 1 }
}

export const resolveMaxCapacity = (maxCapacity?: number | null) =>
  maxCapacity || Number.MAX_SAFE_INTEGER

export function validateSelections(
  variants: NexperienceVariant[],
  selectedOptions: PaxSelectionData['selectedOptions'],
  selectedVariant: PaxSelectionData['selectedVariant']
): boolean {
  if (!selectedOptions) {
    return false
  }

  const options = variants.flatMap(variant => variant.options)
  const totalPersonsSelected = getTotalCount(selectedOptions, options)

  if (totalPersonsSelected === 0) {
    return false
  }

  const maxCapacityForVariant = resolveMaxCapacity(
    variants.find(variant => variant.id === selectedVariant)?.maxCapacity
  )

  if (totalPersonsSelected > maxCapacityForVariant) {
    return false
  }

  return variants
    .flatMap(variant => variant.options)
    .every(option => {
      const selectedCount = selectedOptions[option.id]
      if (!selectedCount) {
        return true
      }

      const testMinimum = selectedCount > 0 && selectedCount >= option.min
      const testMaximum = selectedCount > 0 && selectedCount <= resolveMaxCapacity(option.max)

      return testMinimum && testMaximum
    })
}

const getGroupSize = (
  optionId: string,
  options: { id: string; groupSize?: number | null }[] = []
) => options.find(option => option.id === optionId)?.groupSize ?? 1
